// apps/admin/src/pages/merchants/components/CapabilityToggleCard.tsx
//
// One per-merchant capability switch: a checkbox, optional money ceilings,
// and a Save that PUTs the whole shape back.
//
// Extracted because `WalletBillingTab` carried two ~90-line copies of this
// exact state machine (withdraw permission, card payment) and a third was
// about to land. Worse than the duplication: the two copies had already
// drifted — the card-payment one sent only `{ enabled }`, silently clearing
// the per-charge and daily ceilings the backend accepts and enforces, so
// every whitelisted merchant ended up with UNLIMITED card spend. A single
// component with the ceilings built in is what stops that recurring.
//
// Ceilings are entered in DOLLARS and sent in cents. An empty box means no
// limit (`null`), which is what the backend already treats absence as — the
// hint under the field says so, because "blank = unlimited" is not a safe
// thing for an operator to have to guess.

import { useCallback, useState, type JSX } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Input, Spinner, useToast } from '@printway/ui';

import { apiClient, ApiError } from '../../../lib/apiClient.js';
import { newIdempotencyKey } from '../../../lib/idempotencyKey.js';

type TFn = (key: string, vars?: Record<string, unknown>) => string;

/** One optional money ceiling rendered as a dollar input. */
export interface CapabilityLimitField {
  /** Wire field name, e.g. `max_per_order_cents`. Sent in CENTS. */
  name: string;
  label: string;
  /** Current value in cents, or null for "no limit". */
  valueCents: number | null;
}

export interface CapabilityToggleCardProps {
  /** `GET`/`PUT` target, e.g. `/admin/merchants/:id/card-payment-permission`. */
  endpoint: string;
  /** React Query key for the GET; also what the save invalidates. */
  queryKey: readonly unknown[];
  /**
   * Other views a save makes stale (prefix keys), e.g. the whitelist list and
   * its audit log for card payment. Without them those screens kept showing
   * the pre-save state until their cache expired.
   */
  relatedQueryKeys?: ReadonlyArray<readonly unknown[]>;
  heading: string;
  toggleLabel: string;
  hint: string;
  /** Prefix for data-testid: `${testIdPrefix}-toggle`, `-save`, `-enabled`. */
  testIdPrefix: string;
  /** Card container testid (kept stable for existing E2E selectors). */
  cardTestId: string;
  /**
   * Ceiling fields to render. Values come from the GET response by `name`.
   * Omit for a capability with no limits (withdraw permission today).
   */
  limitFields?: ReadonlyArray<{ name: string; label: string }>;
  /** False disables every control — the admin lacks the permission to save. */
  canEdit: boolean;
  /** Shown in place of the Save button when `canEdit` is false. */
  noPermissionLabel: string;
  savedLabel: string;
  saveErrorLabel: string;
  saveLabel: string;
  savingLabel: string;
  /** Hint under the ceiling inputs — "blank means no limit". */
  noLimitHint?: string;
  /**
   * When set, saving an ENABLED capability with an empty ceiling requires
   * ticking this box: empty means unlimited spend, which must be meant.
   */
  noLimitConfirmLabel?: string;
  /** Shown when Save is pressed without that tick. */
  noLimitRequiredLabel?: string;
  /** Shown in place of the controls when the GET fails. */
  loadErrorLabel: string;
  t: TFn;
}

/** The GET response: `enabled` plus whatever ceiling fields the card declares. */
type CapabilityResponse = { enabled?: boolean } & Record<string, unknown>;

/**
 * Dollars string → integer cents, or null for blank.
 *
 * Returns `undefined` for input that is not a number so the caller can refuse
 * to save rather than silently sending `null` and REMOVING a ceiling the
 * operator thought they were editing.
 */
export function dollarsToCents(raw: string): number | null | undefined {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  // Parsed as text, not via `Number(x) * 100`: the float product is off for
  // ordinary amounts (1.005 * 100 = 100.4999…, so $1.005 became 100 cents),
  // and rounding a third decimal away silently stored a ceiling the operator
  // never typed. More than two decimals is refused instead.
  const m = /^(\d+)(?:\.(\d{1,2}))?$/.exec(trimmed);
  if (!m) return undefined;
  const cents = Number(m[1]) * 100 + Number((m[2] ?? '').padEnd(2, '0'));
  return Number.isSafeInteger(cents) ? cents : undefined;
}

/** Integer cents → dollars string for the input box; null → empty. */
export function centsToDollars(cents: number | null | undefined): string {
  // 0 means "no ceiling" server-side (legacy rows); showing "0" read as a
  // zero limit and then blocked every save, since $0 is refused.
  if (typeof cents !== 'number' || !Number.isFinite(cents) || cents <= 0) return '';
  return String(cents / 100);
}

export function CapabilityToggleCard(props: CapabilityToggleCardProps): JSX.Element {
  const {
    endpoint,
    queryKey,
    relatedQueryKeys,
    heading,
    toggleLabel,
    hint,
    testIdPrefix,
    cardTestId,
    limitFields = [],
    canEdit,
    noPermissionLabel,
    savedLabel,
    saveErrorLabel,
    saveLabel,
    savingLabel,
    noLimitHint,
    noLimitConfirmLabel,
    noLimitRequiredLabel,
    loadErrorLabel,
  } = props;

  const queryClient = useQueryClient();
  const { toast } = useToast();
  const permQuery = useQuery<CapabilityResponse, ApiError>({
    queryKey,
    queryFn: () => apiClient.get<CapabilityResponse>(endpoint),
    staleTime: 30_000,
  });

  const serverEnabled = permQuery.data?.enabled ?? false;
  const [draftEnabled, setDraftEnabled] = useState<boolean | null>(null);
  /** Dollar strings keyed by wire field name; null = untouched since load. */
  const [draftLimits, setDraftLimits] = useState<Record<string, string> | null>(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  // An unsaved edit must not follow the operator to another merchant.
  //
  // This card is NOT remounted when the merchant changes: `MerchantDetailPage`
  // only shows its spinner while the merchant query has no data, so navigating
  // to a merchant that is still cached re-renders this component in place with
  // a new `endpoint`. The drafts are plain state, so a half-typed ceiling —
  // or a ticked toggle — carries over and the next Save writes merchant A's
  // numbers onto merchant B. The ceilings are a spend limit, so that is a
  // money edit made against the wrong account.
  //
  // Reset during render rather than in an effect: an effect would let one
  // frame paint the previous merchant's draft, which is the frame an operator
  // clicks Save in. (This is React's documented "adjusting state when a prop
  // changes" pattern.)
  const [identity, setIdentity] = useState(endpoint);
  if (identity !== endpoint) {
    setIdentity(endpoint);
    setDraftEnabled(null);
    setDraftLimits(null);
    setMsg(null);
  }

  const effectiveEnabled = draftEnabled ?? serverEnabled;
  const serverLimits: Record<string, string> = {};
  for (const f of limitFields) {
    serverLimits[f.name] = centsToDollars(
      permQuery.data?.[f.name] as number | null | undefined,
    );
  }
  const effectiveLimits = draftLimits ?? serverLimits;

  const limitsDirty = limitFields.some(
    (f) => (effectiveLimits[f.name] ?? '') !== (serverLimits[f.name] ?? ''),
  );
  const dirty = (draftEnabled !== null && draftEnabled !== serverEnabled) || limitsDirty;
  // Only a save that changes something and leaves a ceiling empty on an
  // enabled capability asks; re-confirming an untouched setup would be noise.
  const needsNoLimitConfirm =
    noLimitConfirmLabel !== undefined &&
    effectiveEnabled &&
    dirty &&
    limitFields.some((f) => (effectiveLimits[f.name] ?? '').trim() === '');
  // The tick is for WHICH blanks, on WHICH merchant.
  //
  // Reset only when no confirmation was needed any more, it carried
  // over whenever a blank moved: confirm with per-order empty, clear per-day,
  // fill per-order, and the tick (given for per-order) now waved through an
  // unlimited per-DAY ceiling nobody agreed to. Watching the signature
  // makes any change to the set of blanks, or to the merchant, an
  // unconfirmed state on the very render it happens — no effect, so no frame
  // where a stale tick is live.
  const blankSignature = needsNoLimitConfirm
    ? `${endpoint}#${limitFields
        .filter((f) => (effectiveLimits[f.name] ?? '').trim() === '')
        .map((f) => f.name)
        .join(',')}`
    : '';
  const [confirmedFor, setConfirmedFor] = useState(blankSignature);
  const [ticked, setTicked] = useState(false);
  // Any change voids the tick — including moving back to a set that was
  // ticked earlier: the operator has to agree again to what is on screen now.
  if (confirmedFor !== blankSignature) {
    setConfirmedFor(blankSignature);
    setTicked(false);
  }
  const noLimitConfirmed = blankSignature !== '' && ticked;

  const handleSave = useCallback(async (): Promise<void> => {
    // Last line of defence: the PUT replaces the document, so saving a form
    // built from an unknown current value is a write of guesses.
    if (permQuery.isError) return;
    // Build the FULL shape. Sending a partial body is what cleared the
    // ceilings before: the BE replaces the whole permission document.
    const body: Record<string, unknown> = { enabled: effectiveEnabled };
    for (const f of limitFields) {
      const cents = dollarsToCents(effectiveLimits[f.name] ?? '');
      // $0 is refused too: the server reads a stored 0 as "no ceiling", so
      // an operator typing 0 to block card spend would open it instead.
      if (cents === undefined || cents === 0) {
        setMsg({ ok: false, text: saveErrorLabel });
        return;
      }
      body[f.name] = cents;
    }
    if (needsNoLimitConfirm && !noLimitConfirmed) {
      setMsg({ ok: false, text: noLimitRequiredLabel ?? saveErrorLabel });
      return;
    }

    setSaving(true);
    setMsg(null);
    try {
      await apiClient.put(endpoint, body, {
        headers: {
          // Money-adjacent mutation — BE requires a UUID v4 Idempotency-Key.
          'Idempotency-Key': newIdempotencyKey(),
        },
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey }),
        ...(relatedQueryKeys ?? []).map((k) =>
          queryClient.invalidateQueries({ queryKey: k }),
        ),
      ]);
      setDraftEnabled(null);
      setDraftLimits(null);
      toast({ variant: 'success', title: savedLabel });
    } catch {
      // The input was valid and the write failed → toast. A bad amount is
      // field-level and stays inline (the check above).
      toast({ variant: 'error', title: saveErrorLabel });
    } finally {
      setSaving(false);
    }
  }, [
    endpoint,
    queryKey,
    relatedQueryKeys,
    queryClient,
    toast,
    effectiveEnabled,
    effectiveLimits,
    limitFields,
    savedLabel,
    saveErrorLabel,
    permQuery.isError,
    needsNoLimitConfirm,
    noLimitConfirmed,
    noLimitRequiredLabel,
  ]);

  // A FAILED GET is not "everything is off".
  //
  // `permQuery.data` is undefined after an error, so `serverEnabled` reads
  // false and every ceiling box reads blank — a form that looks exactly like
  // a merchant who has never been whitelisted. Saving that writes
  // `{enabled: false, nulls}` over a permission that may well be open with
  // ceilings set, turning a read failure into a silent revocation. So the
  // whole card goes read-only until the current value is actually known.
  const loadFailed = permQuery.isError;
  const controlsDisabled =
    permQuery.isPending || loadFailed || saving || !canEdit;

  return (
    <div
      className="rounded border border-pw-border bg-pw-surface p-4"
      data-testid={cardTestId}
    >
      <h2 className="text-sm font-semibold text-pw-text">{heading}</h2>

      <label className="mt-3 flex items-center gap-2 text-sm text-pw-text">
        <input
          type="checkbox"
          checked={effectiveEnabled}
          disabled={controlsDisabled}
          onChange={(e) => setDraftEnabled(e.target.checked)}
          data-testid={`${testIdPrefix}-toggle`}
        />
        <span data-testid={`${testIdPrefix}-enabled`}>{toggleLabel}</span>
      </label>
      <p className="mt-1 text-xs text-pw-text-muted">{hint}</p>

      {limitFields.length > 0 ? (
        <div className="mt-3 space-y-2">
          <div className="flex flex-wrap gap-3">
            {limitFields.map((f) => (
              <label key={f.name} className="flex flex-col gap-1 text-xs text-pw-text">
                <span className="text-pw-text-muted">{f.label}</span>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  inputMode="decimal"
                  value={effectiveLimits[f.name] ?? ''}
                  disabled={controlsDisabled || !effectiveEnabled}
                  onChange={(e) =>
                    setDraftLimits({
                      ...effectiveLimits,
                      [f.name]: e.target.value,
                    })
                  }
                  data-testid={`${testIdPrefix}-${f.name}`}
                  className="w-40"
                />
              </label>
            ))}
          </div>
          {noLimitHint ? (
            <p className="text-xs text-pw-text-muted">{noLimitHint}</p>
          ) : null}
          {needsNoLimitConfirm ? (
            <label className="flex items-center gap-2 text-xs text-pw-text">
              <input
                type="checkbox"
                checked={noLimitConfirmed}
                disabled={controlsDisabled}
                onChange={(e) =>
                  setTicked(e.target.checked)
                }
                data-testid={`${testIdPrefix}-no-limit-confirm`}
              />
              {noLimitConfirmLabel}
            </label>
          ) : null}
        </div>
      ) : null}

      <div className="mt-3 flex items-center gap-3">
        {loadFailed ? (
          <span
            className="text-xs text-red-600 dark:text-red-300"
            role="alert"
            data-testid={`${testIdPrefix}-load-error`}
          >
            {loadErrorLabel}
          </span>
        ) : canEdit ? (
          <Button
            type="button"
            disabled={!dirty || saving || loadFailed}
            onClick={() => void handleSave()}
            data-testid={`${testIdPrefix}-save`}
          >
            {saving ? <Spinner size="sm" /> : null}
            {saving ? savingLabel : saveLabel}
          </Button>
        ) : (
          // Rendering an enabled Save for an admin who cannot save it just
          // trades a visible refusal for a 403 after the click.
          <span
            className="text-xs text-pw-text-muted"
            data-testid={`${testIdPrefix}-no-permission`}
          >
            {noPermissionLabel}
          </span>
        )}
        {msg ? (
          <span
            className={
              msg.ok
                ? 'text-xs text-green-600 dark:text-green-300'
                : 'text-xs text-red-600 dark:text-red-300'
            }
          >
            {msg.text}
          </span>
        ) : null}
      </div>
    </div>
  );
}

export default CapabilityToggleCard;
